import { CheckCircle2, XCircle, Info } from "lucide-react";
import StepShell from "./StepShell";
import { PROTECTION_PRODUCTS } from "../../data/protectionProducts";

/**
 * Step 9 — Optional Protection.
 *
 * These are optional offers — the customer can advance without making a
 * decision on any of them. Same in-card Accept / Decline / Learn more
 * pattern as required protection, but no confirmation modals because
 * declining is not destructive.
 *
 * Order matches the user spec exactly:
 *   Platinum, Gold, Silver, Powertrain, 5 in 1, Tire & Wheel,
 *   Paintless Dent Repair, Windshield Repair
 */
const OPTIONAL_SLUGS = [
  "platinum-coverage",
  "gold-coverage",
  "silver-coverage",
  "powertrain-coverage",
  "5-in-1-combo",
  "tire-wheel-protection",
  "paintless-dent-repair",
  "windshield-repair",
];

export default function OptionalProtectionStep({ byod, onContinue, onBack, firstName }) {
  const section = byod.state.optional_protection || { states: {}, expanded: {} };
  const states = section.states || {};
  const expanded = section.expanded || {};

  const products = OPTIONAL_SLUGS.map((slug) => PROTECTION_PRODUCTS.find((p) => p.slug === slug)).filter(Boolean);

  const writeState = (slug, patch) => {
    byod.replaceSection("optional_protection", {
      ...section,
      states: { ...states, [slug]: { ...(states[slug] || {}), ...patch } },
    });
  };

  const togglePanel = (slug, panel) => {
    const cur = expanded[slug];
    byod.replaceSection("optional_protection", {
      ...section,
      expanded: { ...expanded, [slug]: cur === panel ? null : panel },
    });
  };

  return (
    <StepShell
      title={firstName ? `${firstName}, here are the optional add-ons.` : "Here are the optional add-ons."}
      subtitle="None of these are required to move forward. Accept anything you want included, skip anything you don't."
      onBack={onBack}
      onContinue={onContinue}
      continueLabel="Continue"
      testIdPrefix="byod-optional-protection-step"
    >
      <div className="grid md:grid-cols-2 gap-4" data-testid="byod-optional-protection-grid">
        {products.map((product) => {
          const st = states[product.slug] || {};
          const panel = expanded[product.slug];
          const isAccepted = st.status === "accepted";
          const isDeclined = st.status === "declined";
          const displayPrice = Number(product.price) || null;

          return (
            <div
              key={product.slug}
              className={`relative bg-white border-2 rounded-2xl p-4 flex flex-col transition ${
                isAccepted ? "border-emerald-500 bg-emerald-50/40"
                : "border-[var(--ssc-line)]"
              }`}
              data-testid={`byod-optional-card-${product.slug}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${
                  isAccepted ? "bg-emerald-500 text-white" : "bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)]"
                }`}>
                  <product.Icon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-extrabold text-[var(--ssc-navy)] leading-tight">{product.name}</div>
                  {displayPrice && (
                    <div className="text-sm font-black text-[var(--ssc-navy)] mt-0.5">${displayPrice.toLocaleString()}</div>
                  )}
                  <p className="text-xs text-[var(--ssc-ink-2)] mt-1.5 leading-snug">{product.shortDesc}</p>
                </div>
              </div>

              {isAccepted && (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-emerald-700" data-testid={`byod-optional-accepted-${product.slug}`}>
                  <CheckCircle2 size={14} /> Added to your deal
                </div>
              )}
              {isDeclined && (
                <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-[var(--ssc-ink-2)]" data-testid={`byod-optional-declined-${product.slug}`}>
                  <XCircle size={14} /> Declined
                </div>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => writeState(product.slug, { status: isAccepted ? null : "accepted", finalPrice: displayPrice || 0 })}
                  className={`ssc-btn !py-2 !text-xs ${isAccepted ? "ssc-btn-primary" : "ssc-btn-outline"}`}
                  data-testid={`byod-optional-accept-${product.slug}`}
                >
                  {isAccepted ? "Accepted" : "Accept"}
                </button>
                <button
                  type="button"
                  onClick={() => writeState(product.slug, { status: isDeclined ? null : "declined", finalPrice: null })}
                  className={`ssc-btn !py-2 !text-xs ${isDeclined ? "ssc-btn-secondary" : "ssc-btn-outline"}`}
                  data-testid={`byod-optional-decline-${product.slug}`}
                >
                  {isDeclined ? "Declined" : "Decline"}
                </button>
                <button
                  type="button"
                  onClick={() => togglePanel(product.slug, "learn")}
                  className="ssc-btn ssc-btn-ghost !py-2 !text-xs"
                  data-testid={`byod-optional-learn-${product.slug}`}
                  aria-expanded={panel === "learn"}
                >
                  <Info size={13} /> {panel === "learn" ? "Hide details" : "Learn more"}
                </button>
              </div>

              {panel === "learn" && (
                <div className="mt-3 pt-3 border-t border-[var(--ssc-line)]" data-testid={`byod-optional-learn-panel-${product.slug}`}>
                  <p className="text-sm text-[var(--ssc-ink)] leading-relaxed">{product.whatItIs || product.heroText}</p>
                  {product.mayHelpWith?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">May help with</div>
                      <ul className="mt-1 list-disc ml-5 text-sm text-[var(--ssc-ink)] space-y-0.5">
                        {product.mayHelpWith.slice(0, 6).map((item, i) => <li key={i}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {product.limits && <p className="text-xs text-[var(--ssc-ink-2)] mt-3 leading-snug">{product.limits}</p>}
                  <button
                    type="button"
                    onClick={() => togglePanel(product.slug, "learn")}
                    className="text-[11px] font-bold uppercase tracking-widest text-[var(--ssc-blue)] mt-3 hover:underline"
                  >
                    Hide details
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </StepShell>
  );
}
